<template>
  <main v-if="data" class="container">
    <div class="row">
      <div class="col-12 col-xs-10 col-md-12 col-lg-10 col-xl-8 col-xxl-7 mx-auto mt-4 mt-md-5">
        <div class="d-flex align-items-center mb-3">
          <h1 class="h2">
            Vlucht {{ selectedFlight.flight_number }} vanaf {{ selectedFlight.origin.city.name_nl }} naar {{ selectedFlight.destination.city.name_nl }}
          </h1>
        </div>
        <div class="mb-2 d-flex">
          <div class="route me-4">
            <div class="step">
              <NuxtImg
                  alt="Origin icon"
                  width="11"
                  height="11"
                  src="img/arrow.up.forward.svg"
              />
            </div>
            <div class="step">
              <NuxtImg
                  alt="Destination icon"
                  width="11"
                  height="11"
                  src="img/arrow.down.forward.svg"
              />
            </div>
          </div>
          <div class="w-100">
            <div class="mb-3 d-flex align-items-center">
              <div>
                <h2 class="mb-1 h5 small text-uppercase opacity-75">
                  <NuxtLink
                      class="inherit"
                      :to="{ name: 'vluchten-vanaf', params: { name: selectedFlight.origin.city.slug_nl, iata: selectedFlight.origin.iata.toLowerCase() } }"
                      :title="'Vluchten vanaf ' + selectedFlight.origin.city.name_nl">
                    {{ selectedFlight.origin.city.name_nl }}
                  </NuxtLink>
                </h2>
                <p class="h3 mb-1">{{ selectedFlight.origin.iata }}</p>
                <p class="mb-0 small opacity-75">{{ selectedFlight.origin.name }}</p>
              </div>
              <div class="ms-auto text-end">
                <p class="mb-1 h5 small text-uppercase opacity-75">
                  {{ formatDate(selectedFlight.scheduled_departure) }}
                </p>
                <p v-if="(selectedFlight.estimated_departure || selectedFlight.real_departure) && (selectedFlight.estimated_departure || selectedFlight.real_departure !== selectedFlight.scheduled_departure)" class="mb-0">
                  <span class="h4 me-1 text-decoration-line-through opacity-75">{{ formatTime(selectedFlight.scheduled_departure) }}</span>
                  <span class="h3">{{ formatTime(selectedFlight.estimated_departure ?? selectedFlight.real_departure) }}</span>
                  <span v-if="selectedFlight.departure_delay && selectedFlight.departure_delay > 0" class="h6 text-danger d-block d-md-inline pt-1 pt-md-n1 ms-1">+{{ selectedFlight.departure_delay }} min.</span>
                  <span v-else-if="selectedFlight.departure_delay && selectedFlight.departure_delay < 0" class="h6 text-success d-block d-md-inline pt-1 pt-md-n1 ms-1">{{ selectedFlight.departure_delay }} min.</span>
                </p>
                <p v-else class="mb-0 h3">
                  {{ formatTime(selectedFlight.scheduled_departure) }}
                </p>
                <p class="small mt-1 mb-0 opacity-75">
                  UTC{{ getTimezone(selectedFlight.scheduled_departure) }}
                </p>
              </div>
            </div>
            <div class="divider">
              <span>{{ createFormattedDistance(selectedFlight?.flight_distance) }} · {{ createFormattedTime(selectedFlight.flight_duration) }}</span>
            </div>
            <div class="mt-3 d-flex align-items-center">
              <div>
                <h2 class="mb-1 h5 small text-uppercase opacity-75">
                  <NuxtLink
                      class="inherit"
                      :to="{ name: 'vluchten-naar', params: { name: selectedFlight.destination.city.slug_nl, iata: selectedFlight.destination.iata.toLowerCase() } }"
                      :title="'Vluchten naar ' + selectedFlight.destination.city.name_nl">
                    {{ selectedFlight.destination.city.name_nl }}
                  </NuxtLink>
                </h2>
                <p class="h3 mb-1">{{ selectedFlight.destination.iata }}</p>
                <p class="mb-0 small opacity-75">{{ selectedFlight.destination.name }}</p>
              </div>
              <div class="ms-auto text-end flex-shrink-0">
                <p class="mb-1 h5 small text-uppercase opacity-75">
                  {{ formatDate(selectedFlight.scheduled_arrival) }}
                </p>
                <p v-if="(selectedFlight.estimated_arrival || selectedFlight.real_arrival) && (selectedFlight.estimated_arrival || selectedFlight.real_arrival !== selectedFlight.scheduled_arrival)" class="mb-0">
                  <span class="h4 me-1 text-decoration-line-through opacity-75">{{ formatTime(selectedFlight.scheduled_arrival) }}</span>
                  <span class="h3">{{ formatTime(selectedFlight.estimated_arrival ?? selectedFlight.real_arrival) }}</span>
                  <span v-if="selectedFlight.arrival_delay && selectedFlight.arrival_delay > 0" class="h6 text-danger d-block d-md-inline pt-1 pt-md-n1 ms-1">+{{ selectedFlight.arrival_delay }} min.</span>
                  <span v-else-if="selectedFlight.arrival_delay && selectedFlight.arrival_delay < 0" class="h6 text-success d-block d-md-inline pt-1 pt-md-n1 ms-1">{{ selectedFlight.arrival_delay }} min.</span>
                </p>
                <p v-else class="mb-0 h3">
                  {{ formatTime(selectedFlight.scheduled_arrival) }}
                </p>
                <p class="small mt-1 mb-0 opacity-75">
                  UTC{{ getTimezone(selectedFlight.scheduled_arrival) }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="row mb-2">
          <div class="col-12 mt-4">
            <div class="row g-2 mb-4">
              <div class="col-12">
                <h2 class="h6 small fw-bold text-uppercase text-primary mb-1">Vluchtinformatie</h2>
              </div>
              <div class="col-12">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Vluchtstatus</h3>
                  <p v-if="selectedFlight.diverted_to" class="mb-0 fw-bold mb-n1 text-danger">
                    Omgeleid naar {{ selectedFlight.diverted_to.city.name_nl }} ({{ selectedFlight.diverted_to.iata }})
                  </p>
                  <p v-else class="mb-0 fw-bold mb-n1" :class="generateStatusClass(selectedFlight)">
                    <span class="live-animation position-relative" style="top: -2px" v-if="selectedFlight.status === 'Live'">●</span>
                    {{ generateStatus(selectedFlight) }}
                  </p>
                </div>
              </div>
              <div class="col-12" v-if="selectedFlight.status === 'Landed' && selectedFlight.arrival_delay && selectedFlight.arrival_delay > 120">
                <div class="alert alert-danger mb-0" role="alert">
                  <p class="mb-1">Vlucht {{ selectedFlight.flight_number }} op {{ moment(selectedFlight.scheduled_departure).format('D MMM YYYY') }} had meer dan <u>{{ selectedFlight.arrival_delay }} minuten vertraging</u>. Zat jij ook op deze vlucht?</p>
                  <p class="mb-0">Mogelijk heb je recht op compensatie. <a :title="selectedFlight.airline.short_name + ' compensatie berekenen'" :href="'https://www.airhelp.com/nl/?utm_medium=affiliate&utm_source=pap&utm_campaign=aff-646c6e21f27d6&utm_content=&a_aid=646c6e21f27d6&a_bid=5c94479f&data1=' + selectedFlight.flight_number + '&chan=rewardsflights'" target="_blank" rel="nofollow noopener" class="fw-bold text-primary text-white opacity-80">Klik hier om je compensatie te berekenen</a>.</p>
                </div>
              </div>
              <div class="col-12" v-else-if="selectedFlight.status === 'Canceled' ">
                <div class="alert alert-danger mb-0" role="alert">
                  <p class="mb-1">Vlucht {{ selectedFlight.flight_number }} op {{ moment(selectedFlight.scheduled_departure).format('D MMM YYYY') }} is geannuleerd. Zat jij ook op deze vlucht?</p>
                  <p class="mb-0">Mogelijk heb je recht op compensatie. <a :title="selectedFlight.airline.short_name + ' compensatie berekenen'" :href="'https://www.airhelp.com/nl/?utm_medium=affiliate&utm_source=pap&utm_campaign=aff-646c6e21f27d6&utm_content=&a_aid=646c6e21f27d6&a_bid=5c94479f&data1=' + selectedFlight.flight_number + '&chan=rewardsflights'" target="_blank" rel="nofollow noopener" class="fw-bold text-primary text-white opacity-80">Klik hier om je compensatie te berekenen</a>.</p>
                </div>
              </div>
              <div class="col-12" v-else-if="selectedFlight.diverted_to">
                <div class="alert alert-danger mb-0" role="alert">
                  <p class="mb-1">Vlucht {{ selectedFlight.flight_number }} op {{ moment(selectedFlight.scheduled_departure).format('D MMM YYYY') }} is omgeleid. Zat jij ook op deze vlucht?</p>
                  <p class="mb-0">Mogelijk heb je recht op compensatie. <a :title="selectedFlight.airline.short_name + ' compensatie berekenen'" :href="'https://www.airhelp.com/nl/?utm_medium=affiliate&utm_source=pap&utm_campaign=aff-646c6e21f27d6&utm_content=&a_aid=646c6e21f27d6&a_bid=5c94479f&data1=' + selectedFlight.flight_number + '&chan=rewardsflights'" target="_blank" rel="nofollow noopener" class="fw-bold text-primary text-white opacity-80">Klik hier om je compensatie te berekenen</a>.</p>
                </div>
              </div>
              <div class="col-6">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Vliegmaatschappij</h3>
                  <p class="mb-0 fw-bold mb-n1">
                    <NuxtImg
                        height="18"
                        v-if="selectedFlight.airline.logo_small_url"
                        class="me-1 mt-n1"
                        loading="lazy"
                        :src="selectedFlight.airline.logo_small_url"
                        :alt="selectedFlight.airline.name + ' icon'"
                    />
                    <NuxtLink
                        :to="{ name: 'nl-luchtvaartmaatschappijen-slug', params: { slug: selectedFlight.airline.slug }}"
                        class="inherit fw-bold ms-1"
                        :title="selectedFlight.airline.name">
                      {{ selectedFlight.airline.name }}
                    </NuxtLink>
                  </p>
                </div>
              </div>
              <div class="col-6">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Uitvoerder</h3>
                  <p class="mb-0 fw-bold mb-n1"><NuxtImg
                      height="18"
                      v-if="selectedFlight.operator.logo_small_url"
                      class="me-2 mt-n1"
                      loading="lazy"
                      :src="selectedFlight.operator.logo_small_url"
                      :alt="selectedFlight.operator.name + ' icon'"
                  />
                    <span>{{ selectedFlight.operator.name }}</span>
                  </p>
                </div>
              </div>
            </div>

            <div class="row g-2 mb-4">
              <div class="col-12">
                <h2 class="h6 small fw-bold text-uppercase text-primary mb-1">Vliegtuig</h2>
              </div>
              <div class="col-12 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Model</h3>
                  <p class="mb-0 fw-bold mb-n1">{{ selectedFlight.aircraft?.model ?? 'N.t.b' }}</p>
                </div>
              </div>
              <div v-if="selectedFlight.aircraft" class="col-6 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Reg.</h3>
                  <p class="mb-0 fw-bold mb-n1">
                    <NuxtLink
                        :to="{ name: 'nl-vliegtuigen-slug', params: { slug: selectedFlight.aircraft.slug }}"
                        class="inherit fw-bold"
                        :title="selectedFlight.aircraft.registration + ' ' + selectedFlight.aircraft.model">
                      {{ selectedFlight.aircraft.registration }}
                    </NuxtLink>
                  </p>
                </div>
              </div>
              <div class="col-6 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Leeftijd</h3>
                  <p class="mb-0 fw-bold mb-n1">{{ selectedFlight.aircraft ? formatAge(selectedFlight.aircraft.age) + ' jaar' : '-' }}</p>
                </div>
              </div>
            </div>

            <div class="row overflow-hidden mb-4">
              <div class="col-12">
                <ScriptGoogleAdsense
                    data-ad-client="ca-pub-2456044840697680"
                    data-ad-slot="5727997721"
                    data-full-width-responsive="true"
                />
              </div>
            </div>

            <div class="row g-2 mb-4">
              <div class="col-12">
                <h2 class="h6 small fw-bold text-uppercase text-primary mb-1">Andere {{ selectedFlight.flight_number }} vluchten</h2>
              </div>
              <div class="col-12">
                <div class="card no-hover py-2 pb-0 bg-blurry">
                  <div class="table-responsive-md overflow-y-hidden">
                    <table class="table table-hover align-middle position-relative blur mb-0" itemscope itemtype="https://schema.org/Table">
                      <thead>
                        <tr>
                          <th class="small text-uppercase opacity-75 fw-normal pt-0">Vertrek</th>
                          <th class="small text-uppercase opacity-75 fw-normal pt-0">Van</th>
                          <th class="small text-uppercase opacity-75 fw-normal pt-0">Naar</th>
                          <th class="small text-uppercase opacity-75 fw-normal pt-0">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="flight in data.slice(0, limitFlights)" :key="flight.scheduled_departure" @click="() => { selectedFlight = flight; scrollTop(); }" :class="{'active' : selectedFlight.scheduled_departure === flight.scheduled_departure}">
                          <th>
                            {{ formatDate(flight.scheduled_departure, true) }}
                          </th>
                          <th>
                            <span class="d-block">{{ flight.origin.iata}}</span>
                            <span class="small opacity-75">{{ flight.origin.city.name_nl}}</span>
                          </th>
                          <th>
                            <span class="d-block">{{ flight.destination.iata}}</span>
                            <span class="small opacity-75">{{ flight.destination.city.name_nl}}</span>
                          </th>
                          <th>
                            <span class="badge rounded-pill small border mb-0 bg-blurry inherit" :class="sanitizeTitle('status-'+ flight.status)">
                              <span class="live-animation" v-if="flight.status === 'Live'">●</span>
                              {{ translateStatus(flight.status) }} {{ flight.selectedFlight }}
                            </span>
                          </th>
                        </tr>
                      </tbody>
                      <tfoot class="blur" v-if="!authStore.user && limitFlights < data.length">
                      <tr v-for="n in 4">
                        <th>
                          {{ moment().format('D MMM YYYY') }}
                        </th>
                        <th>
                          <span class="d-block">AMS</span>
                        </th>
                        <th>
                          <span class="d-block">JFK</span>
                        </th>
                        <th>
                            <span class="badge rounded-pill small border mb-0 bg-blurry inherit status-landed">
                              Landed
                            </span>
                        </th>
                      </tr>
                      </tfoot>
                    </table>
                    <div v-if="!authStore.user && limitFlights < data.length" class="table-disclaimer">
                      <div class="bg-transparent text-center">
                        <NuxtImg
                            class="mb-2"
                            alt="Pilot Emoji"
                            width="30"
                            height="30"
                            loading="lazy"
                            src="img/pilot-emoji.png"
                        />
                        <p class="h3 text-primary">Meer zien? Meld je aan!</p>
                        <p class="mb-0">Voor volledige toegang, maak een <b>gratis account</b> of <b>log in</b>.</p>
                        <p class="mb-0">Klik op <b>Account</b> in het menu om in te loggen.</p>
                      </div>
                    </div>
                    <div class="text-center mb-3 pt-3 border-top" v-if="authStore.user && limitFlights < data.length">
                      <button @click="increaseLimit" role="button" class="badge bg-primary-darken border rounded-pill">Meer vluchten laden</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="row mb-6 gy-2" itemscope itemtype="https://schema.org/FAQPage">
              <div class="col-12">
                <h2 class="h6 small fw-bold text-uppercase text-primary mb-1">Veelgestelde vragen</h2>
              </div>
              <Question>
                <template v-slot:question>
                  Welke luchtvaartmaatschappij voert vluchtnummer {{ selectedFlight.flight_number }} uit?
                </template>
                <template v-slot:answer>
                  <p class="mb-0">{{ selectedFlight.airline.short_name }} vlucht {{ selectedFlight.flight_number }} die op {{ moment(selectedFlight.scheduled_departure).format('D MMM YYYY') }} vertrekt, wordt uitgevoerd door {{ selectedFlight.operator.name }}.</p>
                </template>
              </Question>
              <Question>
                <template v-slot:question>
                  Naar welke bestemming vliegt vluchtnummer {{ selectedFlight.flight_number }}?
                </template>
                <template v-slot:answer>
                  <p class="mb-0">De bestemming van {{ selectedFlight.flight_number }} is {{ selectedFlight.destination.name }} in {{ selectedFlight.destination.city.name_nl }} ({{ selectedFlight.destination.city.country.name_nl }}). Deze <NuxtLink :title="selectedFlight.airline.short_name + ' ' + selectedFlight.origin.city.name_nl + ' naar ' + selectedFlight.destination.city.name_nl" :to="{ name: 'vluchten-tussen', params: { name: selectedFlight.origin.city.slug_nl, iata: selectedFlight.origin.iata.toLowerCase(), toname: selectedFlight.destination.city.slug_nl, toiata: selectedFlight.destination.iata.toLowerCase() } }">{{ selectedFlight.airline.short_name }} vlucht van {{ selectedFlight.origin.city.name_nl }} naar {{ selectedFlight.destination.city.name_nl }}</NuxtLink> duurt ongeveer {{ createFormattedTime(selectedFlight.flight_duration) }}.</p>
                </template>
              </Question>
              <Question v-if="selectedFlight.aircraft">
                <template v-slot:question>
                  Met welk vliegtuig wordt vlucht {{ selectedFlight.flight_number }} naar {{ selectedFlight.destination.city.name_nl }} uitgevoerd?
                </template>
                <template v-slot:answer>
                  <p class="mb-0">De vlucht naar {{ selectedFlight.destination.city.name_nl }} met {{ selectedFlight.airline.short_name }} op {{ moment(selectedFlight.scheduled_departure).format('D MMM YYYY') }} wordt uitgevoerd in een {{ selectedFlight.aircraft.model }}.</p>
                </template>
              </Question>
              <Question>
                <template v-slot:question>
                  Wat kan ik doen als mijn {{ selectedFlight.airline.short_name }} vlucht naar {{ selectedFlight.destination.city.name_nl }} vertraagd of geannuleerd is? Heb ik recht op een compensatie?
                </template>
                <template v-slot:answer>
                  <p>
                    Is je {{ selectedFlight.airline.short_name }} vlucht vertraagd of geannuleerd? In veel gevallen heb je recht op een compensatie die kan oplopen tot € 600 per passagier.
                  </p>
                  <p>De compensatie is afhankelijk van de lengte van je vlucht. Hoe verder je vliegt, hoe hoger je compensatie en hoe meer geld je terug krijgt.</p>
                  <ul>
                    <li>Minder dan 1.500 km: € 250.</li>
                    <li>Tussen 1.500 en 3.500 km: € 400.</li>
                    <li>Meer dan 3.500 km: € 600.</li>
                  </ul>
                  <p>Is je {{ selectedFlight.airline.short_name }} vlucht naar {{ selectedFlight.destination.city.name_nl }} vertraagd of geannuleerd? Controleer dan of je aanspraak maakt op een compensatie. Wij raden aan om gebruik te maken van AirHelp.com.</p>
                  <a :title="selectedFlight.airline.short_name + ' compensatie berekenen'"
                      :href="'https://www.airhelp.com/nl/?utm_medium=affiliate&utm_source=pap&utm_campaign=aff-646c6e21f27d6&utm_content=&a_aid=646c6e21f27d6&a_bid=5c94479f&data1=' + selectedFlight.flight_number + '&chan=rewardsflights'" target="_blank" rel="nofollow noopener" class="btn btn-primary fw-bold mt-2">Bereken je compensatie</a>
                </template>
              </Question>
              <Question v-if="selectedFlight.origin.city.country.continent != selectedFlight.destination.city.country.continent">
                <template v-slot:question>
                  Hoe laat van tevoren moet ik op {{ selectedFlight.origin.name }} zijn voor vlucht {{ selectedFlight.flight_number }}?
                </template>
                <template v-slot:answer>
                  <p class="mb-0">Voor een <a title="Wat is een intercontinentale vlucht?" href="https://rewards.flights/nl/blog/wat-is-een-intercontinentale-vlucht-en-hoelang-van-tevoren-op-luchthaven">intercontinentale vlucht</a> vanaf {{ selectedFlight.origin.name }} naar {{ selectedFlight.destination.name }} wordt aanbevolen om minimaal 3 uur van tevoren aanwezig te zijn. Voor specifieke richtlijnen van {{ selectedFlight.operator.short_name ?? selectedFlight.operator.name }}, controleer altijd de instructies voor de meest accurate informatie.</p>
                </template>
              </Question>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
<script setup lang="ts">
import moment from "moment/moment";
import 'moment/locale/nl';
import {useAuthStore} from "~/store/auth";

const route = useRoute()
const runtimeConfig = useRuntimeConfig()

const {data: fetchedData} = await useAsyncData(
    'flight-' + route.params.slug,
    async () => {
      return $fetch(runtimeConfig.public.backendUrl + "/api/v2/flights/" + route.params.slug, {
        headers: {
          'User-Agent': 'RewardsFlightsFrontend/1.0 (https://rewards.flights)',
        }
      })
    }
);

if (!fetchedData.value.flight?.length) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Flight not found'
  })
}

const data = ref(fetchedData.value.flight);

const latestFlightTodayOrNext = computed(() => {
  const now = moment();
  const today = moment().startOf('day').local();

  // Filter vluchten voor alleen vandaag
  const todaysFlights = data.value.filter(flight =>
      moment(flight.scheduled_departure).local().isSame(today, 'day')
  );

  // Controleer of er een vlucht gaande is
  const ongoingFlight = todaysFlights.find(flight =>
      moment(flight.scheduled_departure).local().isSameOrBefore(now) &&
      moment(flight.scheduled_arrival).local().isAfter(now)
  );

  if (ongoingFlight) {
    return ongoingFlight;
  }

  // Zoek naar de meest recente vlucht van vandaag die al geweest is
  const pastFlightsToday = todaysFlights.filter(flight =>
      moment(flight.scheduled_departure).local().isBefore(now)
  ).sort((a, b) => moment(b.scheduled_departure).local().diff(moment(a.scheduled_departure).local()));

  if (pastFlightsToday.length > 0) {
    return pastFlightsToday[0]; // De meest recente vlucht van vandaag die al geweest is
  }

  // Zoek naar de eerstvolgende vlucht na nu
  const futureFlights = data.value
      .filter(flight => moment(flight.scheduled_departure).local().isAfter(now))
      .sort((a, b) => moment(a.scheduled_departure).local().diff(moment(b.scheduled_departure).local()));

  if (futureFlights.length > 0) {
    return futureFlights[0]; // De eerstvolgende toekomstige vlucht
  }

  // Als er geen toekomstige vlucht is, zoek dan naar de meest recente vlucht uit het verleden
  const pastFlights = data.value
      .filter(flight => moment(flight.scheduled_departure).local().isBefore(now))
      .sort((a, b) => moment(b.scheduled_departure).local().diff(moment(a.scheduled_departure).local()));

  return pastFlights.length > 0 ? pastFlights[0] : null; // De meest recente vlucht uit het verleden
});

const _selectedFlight = ref(latestFlightTodayOrNext.value);

const selectedFlight = computed({
  get: () => _selectedFlight.value,
  set: (val) => {
    _selectedFlight.value = val;
  }
});

const limitFlights = ref(5);

const increaseLimit = () => {
  limitFlights.value += 25;
};

const authStore = useAuthStore();

defineOgImage({
  component: 'route',
  description: latestFlightTodayOrNext.value.flight_number + ' • ' + latestFlightTodayOrNext.value.airline.name,
  title: latestFlightTodayOrNext.value.origin.iata + ' - ' + latestFlightTodayOrNext.value.destination.iata,
});

useSeoMeta({
  title: "✈ Vlucht " + latestFlightTodayOrNext.value.flight_number + " van " + latestFlightTodayOrNext.value.origin.city.name_nl + " (" + latestFlightTodayOrNext.value.origin.iata + ") naar " + latestFlightTodayOrNext.value.destination.city.name_nl + " (" + latestFlightTodayOrNext.value.destination.iata + ")",
  ogTitle: "✈ Vlucht " + latestFlightTodayOrNext.value.flight_number + " van " + latestFlightTodayOrNext.value.origin.city.name_nl + " (" + latestFlightTodayOrNext.value.origin.iata + ") naar " + latestFlightTodayOrNext.value.destination.city.name_nl + " (" + latestFlightTodayOrNext.value.destination.iata + ")",
  twitterTitle: "✈ Vlucht " + latestFlightTodayOrNext.value.flight_number + " van " + latestFlightTodayOrNext.value.origin.city.name_nl + " (" + latestFlightTodayOrNext.value.origin.iata + ") naar " + latestFlightTodayOrNext.value.destination.city.name_nl + " (" + latestFlightTodayOrNext.value.destination.iata + ")",
  description: "Vluchtstatus van " + latestFlightTodayOrNext.value.airline.name + " vlucht " + latestFlightTodayOrNext.value.flight_number + " van " + latestFlightTodayOrNext.value.origin.city.name_nl + " naar " + latestFlightTodayOrNext.value.destination.city.name_nl + ": realtime informatie over vertrek, aankomst, vertragingen en annuleringen.",
  ogDescription: "Vluchtstatus van " + latestFlightTodayOrNext.value.airline.name + " vlucht " + latestFlightTodayOrNext.value.flight_number + " van " + latestFlightTodayOrNext.value.origin.city.name_nl + " naar " + latestFlightTodayOrNext.value.destination.city.name_nl + ": realtime informatie over vertrek, aankomst, vertragingen en annuleringen.",
  twitterDescription: "Vluchtstatus van " + latestFlightTodayOrNext.value.airline.name + " vlucht " + latestFlightTodayOrNext.value.flight_number + " van " + latestFlightTodayOrNext.value.origin.city.name_nl + " naar " + latestFlightTodayOrNext.value.destination.city.name_nl + ": realtime informatie over vertrek, aankomst, vertragingen en annuleringen.",
  ogUrl: runtimeConfig.public.baseUrl + "/nl/vluchten/" + data.value[0].flight_number.toLowerCase(),
  ogLocale: "nl",
  ogLocaleAlternate: "en",
});

useHead({
  htmlAttrs: {
    lang: 'nl'
  },
  link: [
    {
      rel: "canonical",
      href: runtimeConfig.public.baseUrl + "/nl/vluchten/" + data.value[0].flight_number.toLowerCase()
    },
    {
      rel: "alternate",
      hreflang: "x-default",
      href: runtimeConfig.public.baseUrl + "/flights/" + data.value[0].flight_number.toLowerCase()
    },
    {
      rel: "alternate",
      hreflang: "en",
      href: runtimeConfig.public.baseUrl + "/flights/" + data.value[0].flight_number.toLowerCase()
    },
    {
      rel: "alternate",
      hreflang: "nl",
      href: runtimeConfig.public.baseUrl + "/nl/vluchten/" + data.value[0].flight_number.toLowerCase()
    }
  ],
});

useJsonld(() => {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: runtimeConfig.public.baseUrl + '/nl',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Luchtvaartmaatschappijen',
            item: runtimeConfig.public.baseUrl + '/nl/luchtvaartmaatschappijen',
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: data.value[0].airline.name,
            item: runtimeConfig.public.baseUrl + '/nl/luchtvaartmaatschappijen/' + data.value[0].airline.slug,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: data.value[0].flight_number,
          },
        ],
      },
      {
        '@type': 'Flight',
        flightNumber: data.value[0].flight_number,
        aircraft: data.value[0].aircraft?.model,
        departureAirport: {
          '@type': 'Airport',
          iataCode: data.value[0].origin.iata,
          name: data.value[0].origin.name,
        },
        arrivalAirport: {
          '@type': 'Airport',
          iataCode: data.value[0].destination.iata,
          name: data.value[0].destination.name,
        },
      }
    ],
  }
});

const createFormattedTime = (durationInMinutes: string) => computed(() => {
  let duration = moment.duration(durationInMinutes, 'minutes');
  return `${Math.floor(duration.asHours())}h ${duration.minutes()}m`;
});

const createFormattedDistance = (distance: string) => computed(() => {
  if (distance === undefined || distance === null) {
    return;
  }
  return Intl.NumberFormat("nl").format(Number(distance)) + ' km';
});

function formatDate(date: string | null, localTime: boolean = false) {
  if (date === null) {
    return '-';
  }

  const parsedDate = moment(date);
  const today = moment().startOf('day');
  const yesterday = moment().subtract(1, 'days').startOf('day');
  const tomorrow = moment().add(1, 'days').startOf('day');

  if (parsedDate.isSame(today, 'day')) {
    return `Vandaag`;
  } else if (parsedDate.isSame(yesterday, 'day')) {
    return `Gisteren`;
  } else if (parsedDate.isSame(tomorrow, 'day')) {
    return `Morgen`;
  }

  if(localTime) {
    return parsedDate.format('D MMM YYYY');
  }

  return moment(date).format('D MMM YYYY');
}

function formatTime(date: string | null) {
  if (date === null) {
    return '-';
  }

  return moment.parseZone(date).format('HH:mm');
}

function getTimezone(date: string | null) {
  if (date === null) {
    return '-';
  }

  return moment.parseZone(date).format('Z');
}

function formatAge(age: string | null) {
  if (age === null) {
    return '-';
  }

  const ageDate = moment(age);
  const now = moment();

  return now.diff(ageDate, 'years');
}

function sanitizeTitle(title: string) {
  let slug = "";
  const titleLower = title.toLowerCase();
  slug = titleLower.replace(/\s*$/g, '');
  slug = slug.replace(/\s+/g, '-');
  return slug;
}

function translateStatus(status: string) {
  if (status === 'Scheduled') {
    return 'Gepland';
  } else if (status === 'Landed') {
    return 'Geland';
  } else if (status === 'Diverted') {
    return 'Omgeleid';
  } else if (status === 'Canceled') {
    return 'Geannuleerd';
  }

  return status
}

function generateStatus(flight: object) {
  if (flight.status === 'Scheduled') {
    return 'Gepland';
  } else if (flight.status === 'Landed') {
    if(flight.arrival_delay && flight.arrival_delay > 15){
      return 'Geland met '+ flight.arrival_delay +' min. vertraging';
    }
    return 'Geland';
  } else if (flight.status === 'Diverted') {
    return 'Omgeleid';
  } else if (flight.status === 'Canceled') {
    return 'Geannuleerd';
  }

  return flight.status
}

function generateStatusClass(flight: object){
  if (flight.status === 'Scheduled') {
    return 'text-white';
  } else if (flight.status === 'Landed') {
    if(flight.arrival_delay && flight.arrival_delay > 15){
      return 'text-warning';
    }
    return 'text-success';
  } else if (flight.status === 'Diverted') {
    return 'text-warning';
  } else if (flight.status === 'Canceled') {
    return 'text-danger';
  }

  return 'text-white';
}

const scrollTop = () => {
  window.scrollTo(0, 0)
}

</script>